import { ActivitySpinnerSize, ActivitySpinnerWidget, ButtonType, ButtonWidget } from '@tableau/widgets';
import React from 'react';

import { Location } from '~commonInfra/utils/Location';
import { Tableau } from '~commonInfra/utils/TableauLog';
import { Messages } from '~src/codegen/Localize';

import { IExecution } from '../serverApi/types/IExecution';
import { ContentRelayPlanDetailsStyles as Styles } from './ContentRelayPlanDetailsStyles';
import { ContentRelayPlanExecutionsGrid } from './ContentRelayPlanExecutionsGrid';
import { appendExecutions, executionKey, EXECUTIONS_PAGE_SIZE, hasInProgressExecution, loadExecutionsPage, mergeExecutions } from './planExecutionsService';

export const ContentRelayPlanExecutionsTabTestIds = {
  Body: 'relay-plan-executions',
  Spinner: 'relay-plan-executions-spinner',
  Empty: 'relay-plan-executions-empty',
  Error: 'relay-plan-executions-error',
  Retry: 'relay-plan-executions-retry',
  ShowMore: 'relay-plan-executions-show-more',
  LoadMoreError: 'relay-plan-executions-load-more-error',
};

/** How often in-progress rows are refreshed. */
export const EXECUTIONS_REFRESH_INTERVAL_MS = 10000;

/** After the Run dialog closes, the extra re-read for a run whose trigger was still in flight at close. */
export const RUN_FOLLOW_UP_REFRESH_MS = 3000;

/**
 * How long a row's Download action stays disabled after a click. Fixed, because the download is a browser
 * navigation and the page can't tell when it finishes. Only stops accidental repeat clicks; not a rate limit.
 */
export const DOWNLOAD_HOLD_MS = 5000;

export interface ContentRelayPlanExecutionsTabProps {
  readonly planLuid: string;
  /** Opens the Run dialog, offered on rows whose package has expired. */
  readonly onRerun: () => void;
  /** Bumped by the owner when a run may have started (the Run dialog closed), so the list re-reads. */
  readonly reloadKey?: number;
  /** Overridable for tests; the default navigates the browser to the download endpoint. */
  readonly onDownload?: (execution: IExecution) => void;
}

function navigateToDownload(execution: IExecution): void {
  Location.instance.setUrl(execution.downloadUrl);
}

/**
 * The Run History tab of the Content Relay plan detail page: the signed-in user's executions of the plan
 * (the server scopes `listExecutions` to the caller), with Download on ready rows and a re-run affordance
 * on expired ones. Rows that are still running refresh until they settle.
 */
export function ContentRelayPlanExecutionsTab(props: ContentRelayPlanExecutionsTabProps): JSX.Element {
  const [executions, setExecutions] = React.useState<ReadonlyArray<IExecution>>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [hasLoadMoreError, setHasLoadMoreError] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);
  // Bumped when the plan changes or the list is reloaded in full, and on unmount: results of older loads
  // (first page, refresh, Show more) are then dropped. Refresh and Show more don't invalidate each other.
  const generationRef = React.useRef(0);
  const isRefreshingRef = React.useRef(false);
  // Rows whose Download is held after a click, by execution key. Kept here, not in the grid, so a list
  // refresh doesn't re-enable them early.
  const [heldDownloads, setHeldDownloads] = React.useState<{ [key: string]: boolean }>({});
  const holdTimersRef = React.useRef<Array<ReturnType<typeof setTimeout>>>([]);

  React.useEffect(function() {
    const timers = holdTimersRef;
    return function() { timers.current.forEach(clearTimeout); };
  }, []);

  const refresh = function(showSpinner: boolean): void {
    const generation = generationRef.current;
    if (showSpinner) {
      setIsLoading(true);
      setHasError(false);
    }
    isRefreshingRef.current = true;
    loadExecutionsPage(props.planLuid, 0)
      .then(function(page) {
        if (generationRef.current === generation) {
          // Keep the pages loaded by Show more; only the first page is replaced.
          setExecutions(function(current) { return showSpinner ? page.executions : mergeExecutions(page.executions, current); });
          setTotalCount(page.totalCount);
          setHasError(false);
          setIsLoading(false);
        }
      })
      .catch(function(error: any) {
        if (generationRef.current !== generation) {
          return;
        }
        Tableau.Logger().info('Content Relay: failed to load executions: ' + error);
        if (showSpinner) {
          setExecutions([]);
          setHasError(true);
        }
        setIsLoading(false);
      })
      .then(function() {
        if (generationRef.current === generation) {
          isRefreshingRef.current = false;
        }
      });
  };

  React.useEffect(function() {
    const generation = generationRef;
    generation.current++;
    isRefreshingRef.current = false;
    setHasLoadMoreError(false);
    setIsLoadingMore(false);
    refresh(true);
    return function() { generation.current++; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.planLuid, reloadToken]);

  // The Run dialog closed: a run may have started, so re-read quietly (no spinner, keeps loaded pages).
  // The dialog can close before triggerExtraction resolves and an idle tab doesn't poll, so read once
  // more shortly after.
  const reloadKey = props.reloadKey;
  const lastReloadKeyRef = React.useRef(reloadKey);
  React.useEffect(function() {
    if (lastReloadKeyRef.current === reloadKey) {
      return undefined;
    }
    lastReloadKeyRef.current = reloadKey;
    refresh(false);
    const timer = setTimeout(function() { refresh(false); }, RUN_FOLLOW_UP_REFRESH_MS);
    return function() { clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const polling = hasInProgressExecution(executions);
  React.useEffect(function() {
    if (!polling) {
      return undefined;
    }
    const timer = setInterval(function() {
      // Skip a tick while the previous refresh is still in flight.
      if (!isRefreshingRef.current) {
        refresh(false);
      }
    }, EXECUTIONS_REFRESH_INTERVAL_MS);
    return function() { clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polling, props.planLuid]);

  const onDownload = function(execution: IExecution): void {
    const key = executionKey(execution);
    if (heldDownloads[key]) {
      return;
    }
    (props.onDownload || navigateToDownload)(execution);
    setHeldDownloads(function(current) { return { ...current, [key]: true }; });
    holdTimersRef.current.push(setTimeout(function() {
      setHeldDownloads(function(current) {
        const next = { ...current };
        delete next[key];
        return next;
      });
    }, DOWNLOAD_HOLD_MS));
  };

  const onShowMore = function(): void {
    const generation = generationRef.current;
    setIsLoadingMore(true);
    setHasLoadMoreError(false);
    loadExecutionsPage(props.planLuid, executions.length, EXECUTIONS_PAGE_SIZE)
      .then(function(page) {
        if (generationRef.current === generation) {
          setExecutions(function(current) { return appendExecutions(current, page.executions); });
          setTotalCount(page.totalCount);
        }
      })
      .catch(function() {
        if (generationRef.current === generation) {
          setHasLoadMoreError(true);
        }
      })
      .then(function() {
        if (generationRef.current === generation) {
          setIsLoadingMore(false);
        }
      });
  };

  if (isLoading) {
    return (
      <div className={Styles.CenteredState}>
        <ActivitySpinnerWidget testId={ContentRelayPlanExecutionsTabTestIds.Spinner} spinnerSize={ActivitySpinnerSize.Large} />
      </div>
    );
  }

  if (hasError) {
    return (
      <div className={Styles.CenteredState} data-tb-test-id={ContentRelayPlanExecutionsTabTestIds.Error}>
        <div>{Messages.relayPlanExecutionsLoadError()}</div>
        <ButtonWidget
          buttonType={ButtonType.Outline}
          onClick={function() { setReloadToken(function(t) { return t + 1; }); }}
          testId={ContentRelayPlanExecutionsTabTestIds.Retry}
        >
          {Messages.tryAgain()}
        </ButtonWidget>
      </div>
    );
  }

  if (!executions.length) {
    return (
      <div className={Styles.CenteredState} data-tb-test-id={ContentRelayPlanExecutionsTabTestIds.Empty}>
        <div className={Styles.StateTitle}>{Messages.relayPlanExecutionsEmptyTitle()}</div>
        <div>{Messages.relayPlanExecutionsEmptyBody()}</div>
      </div>
    );
  }

  return (
    <div className={Styles.OverviewBody} data-tb-test-id={ContentRelayPlanExecutionsTabTestIds.Body}>
      <ContentRelayPlanExecutionsGrid
        executions={executions}
        onDownload={onDownload}
        heldDownloads={heldDownloads}
        onRerun={props.onRerun}
      />
      {totalCount > executions.length && (
        <div className={Styles.Footer}>
          {hasLoadMoreError && (
            <span className={Styles.ErrorText} data-tb-test-id={ContentRelayPlanExecutionsTabTestIds.LoadMoreError}>
              {Messages.relayPlanExecutionsLoadError()}
            </span>
          )}
          <ButtonWidget
            buttonType={ButtonType.LowEmphasis}
            onClick={onShowMore}
            disabled={isLoadingMore}
            showSpinner={isLoadingMore}
            testId={ContentRelayPlanExecutionsTabTestIds.ShowMore}
          >
            {hasLoadMoreError ? Messages.tryAgain() : Messages.relayPlanContentsShowMore()}
          </ButtonWidget>
        </div>
      )}
    </div>
  );
}
